import React, { useState } from 'react';
import { Head, Link, router, useForm } from '@inertiajs/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Plus, X, HelpCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Breadcrumbs } from '@/components/ui/breadcrumbs';

interface Divisor {
  id: number;
  divisor_type: string;
  title: string;
  content_preview: string;
}

interface ExtractJob {
  id?: number;
  name?: string;
  start_page?: number;
  end_page?: number;
  extract_prompt?: string;
  json_map?: string;
  divisor_types?: string[];
  response_payload?: any;
  divisors?: Divisor[];
}

interface Document {
  id: number;
  name: string;
  page_count: number;
  divisor_types?: string[];
  extract_jobs: ExtractJob[];
}

interface ExtractJobFormProps {
  document: Document;
  extractJob: ExtractJob;
  divisorTypes: string[];
  previousExtractJob?: ExtractJob;
  nextExtractJob?: ExtractJob;
}

export default function ExtractJobForm({
  document,
  extractJob,
  divisorTypes,
  previousExtractJob,
  nextExtractJob
}: ExtractJobFormProps) {
  const [selectedDivisors, setSelectedDivisors] = useState<string[]>(
    extractJob.divisor_types || document.divisor_types || []
  );
  const [showHelp, setShowHelp] = useState(false);

  const { data, setData, post, put, processing, errors } = useForm({
    name: extractJob.name || '',
    start_page: extractJob.start_page || 1,
    end_page: extractJob.end_page || document.page_count,
    extract_prompt: extractJob.extract_prompt || '',
    json_map: typeof extractJob.json_map === 'object'
      ? JSON.stringify(extractJob.json_map, null, 2)
      : extractJob.json_map || '',
    divisor_types: extractJob.divisor_types || document.divisor_types || []
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const formData = {
      ...data,
      divisor_types: selectedDivisors
    };

    if (extractJob.id) {
      put(`/documents/${document.id}/extract_jobs/${extractJob.id}`, {
        data: formData
      });
    } else {
      post(`/documents/${document.id}/extract_jobs`, {
        data: formData
      });
    }
  };

  const addDivisor = () => {
    setSelectedDivisors([...selectedDivisors, '']);
  };

  const removeDivisor = (index: number) => {
    setSelectedDivisors(selectedDivisors.filter((_, i) => i !== index));
  };

  const updateDivisor = (index: number, value: string) => {
    const updated = [...selectedDivisors];
    updated[index] = value;
    setSelectedDivisors(updated);
    setData('divisor_types', updated);
  };

  const generateJsonMap = async () => {
    const validDivisors = selectedDivisors.filter(d => d.trim() !== '');

    if (validDivisors.length === 0) {
      setData('json_map', 'Please select at least one divisor type.');
      return;
    }

    try {
      const response = await fetch(`/documents/${document.id}/extract_jobs/generate_json_map`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': window.document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({
          extract_job: { divisor_types_order: validDivisors }
        })
      });

      const result = await response.json();

      if (result.json_map) {
        setData('json_map', JSON.stringify(result.json_map, null, 2));
      } else if (result.error) {
        setData('json_map', `Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error generating JSON map:', error);
      setData('json_map', 'Error generating JSON map. Please try again.');
    }
  };

  const generatePrompt = () => {
    const validDivisors = selectedDivisors
      .filter(d => d.trim() !== '')
      .map(d => d.charAt(0).toUpperCase() + d.slice(1));

    if (validDivisors.length === 0) {
      setData('extract_prompt', 'Please select at least one divisor type to generate a prompt.');
      return;
    }

    const joinWithConjunction = (arr: string[], conjunction = 'and') => {
      if (arr.length === 0) return '';
      if (arr.length === 1) return arr[0];
      if (arr.length === 2) return `${arr[0]} ${conjunction} ${arr[1]}`;
      return `${arr.slice(0, -1).join(', ')}, ${conjunction} ${arr[arr.length - 1]}`;
    };

    const grammaticalDivisors = joinWithConjunction(validDivisors, 'and');

    let promptText = `Extract the ${grammaticalDivisors} metadata for this document following the schema precisely.\n\n`;

    validDivisors.forEach(divisor => {
      promptText += `There should be [[EDIT THIS TEXT]] ${divisor}s in the ${divisor}s array.\n\n`;
      promptText += `${divisor}s are denoted as [[EDIT THIS TEXT]]\n\n`;
      promptText += `An example of a ${divisor} is: \n[[EDIT THIS TEXT]]\nOR\n[[EDIT THIS TEXT]]\n\n`;
    });

    setData('extract_prompt', promptText);
  };

  const isEditing = !!extractJob.id;
  const title = isEditing
    ? `Edit Extract Job for ${document.name}`
    : `New Extract Job for ${document.name}`;

  const helpContent = `
## Prompts 101

### What is a prompt?

Prompt generation is a key part of vectorizing documents with Reducto. The extract prompt is what allows Reducto to efficiently extract a documents hierarchy. A bad prompt equals a bad extract (Garbage In, Garbage Out).

### How do I write a good prompt?

A good prompt outlines the hierarchy of the documents, defines proper examples of the hierarchy types, as well as denotes how hierarchy types are written.

Below is an example of a prompt:
> Extract the chapter and section metadata for this document following the schema precisely. There should be 11 Chapters in the Chapters array starting with Chapter 1.
>
> Chapters will be denoted with a chapter number and bold test for the title. For example:
> Chapter 1 - GENERAL PROVISIONS or Chapter 10 - AMUSEMENTS AND ENTERTAINMENTS
>
> Sections will be nested in chapters and will look like this:
> Sec. 1-1. - How Code designated and cited
> or
> Sec. 22-10. - Time limitation on permits

As you can see, this prompt outlines how many chapters there are, how are they written, as well as a couple of examples for each divisor type.

### How do I generate a message?

Prompt generation is simple! Simply fill out your desired divisor types, and click the generate prompt button! From there, fill in the blanks where you see [[EDIT THIS TEXT]].
  `;

  return (
    <>
      <Head title={title} />

      <div className="container mx-auto p-6 max-w-7xl">
        <div className="space-y-6">
          {/* Breadcrumbs */}
          <Breadcrumbs opsConsole
            items={[
              { label: document.name, href: `/documents/${document.id}` },
              { label: isEditing ? `Edit Extract Job` : 'New Extract Job', current: true }
            ]}
          />

          {/* Header */}
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold">{title}</h1>
            <Button asChild variant="outline">
              <Link href={`/documents/${document.id}`}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Document
              </Link>
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Form Fields */}
            <Card>
              <CardHeader>
                <CardTitle>Extract Job Configuration</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2 space-y-2">
                    <Label htmlFor="name">Name Extract Job</Label>
                    <Input
                      id="name"
                      value={data.name}
                      onChange={(e) => setData('name', e.target.value)}
                      className={errors.name ? 'border-red-500' : ''}
                    />
                    {errors.name && (
                      <p className="text-sm text-red-600">{errors.name}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="start_page">Start Page</Label>
                    <Input
                      id="start_page"
                      type="number"
                      min="1"
                      value={data.start_page}
                      onChange={(e) => setData('start_page', parseInt(e.target.value))}
                      className={errors.start_page ? 'border-red-500' : ''}
                    />
                    {errors.start_page && (
                      <p className="text-sm text-red-600">{errors.start_page}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="end_page">End Page</Label>
                    <Input
                      id="end_page"
                      type="number"
                      min="1"
                      max={document.page_count}
                      value={data.end_page}
                      onChange={(e) => setData('end_page', parseInt(e.target.value))}
                      className={errors.end_page ? 'border-red-500' : ''}
                    />
                    {errors.end_page && (
                      <p className="text-sm text-red-600">{errors.end_page}</p>
                    )}
                  </div>

                  <div className="md:col-span-2 space-y-2">
                    <Label htmlFor="extract_prompt">Extract Prompt</Label>
                    <Textarea
                      id="extract_prompt"
                      value={data.extract_prompt}
                      onChange={(e) => setData('extract_prompt', e.target.value)}
                      className={`min-h-[12rem] ${errors.extract_prompt ? 'border-red-500' : ''}`}
                      placeholder="Extract the chapter and section metadata for this document following the schema precisely..."
                    />
                    {errors.extract_prompt && (
                      <p className="text-sm text-red-600">{errors.extract_prompt}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Divisor Types Selection */}
            <Card>
              <CardHeader>
                <CardTitle>Select Nested Divisor Types</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {selectedDivisors.map((divisor, index) => (
                  <div key={index} className="flex gap-3 items-center">
                    <Select
                      value={divisor}
                      onValueChange={(value) => updateDivisor(index, value)}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select a divisor type" />
                      </SelectTrigger>
                      <SelectContent>
                        {divisorTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => removeDivisor(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}

                <Button
                  type="button"
                  variant="outline"
                  onClick={addDivisor}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Nested Divisor
                </Button>
              </CardContent>
            </Card>

            {/* Prompt Generation */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex gap-3 justify-center items-center">
                  <Button
                    type="button"
                    onClick={generatePrompt}
                    className="bg-green-500 hover:bg-green-600"
                  >
                    Generate Prompt
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowHelp(!showHelp)}
                    className="bg-yellow-500 hover:bg-yellow-600 text-black"
                  >
                    <HelpCircle className="h-4 w-4" />
                  </Button>
                </div>

                {showHelp && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-md">
                    <div className="prose prose-sm max-w-none">
                      {helpContent.split('\n\n').map((paragraph, index) => {
                        if (paragraph.startsWith('## ') || paragraph.startsWith('### ')) {
                          const level = paragraph.startsWith('## ') ? 'text-lg font-semibold' : 'text-md font-medium';
                          return <h3 key={index} className={level}>{paragraph.replace(/^#+\s/, '')}</h3>;
                        }
                        if (paragraph.startsWith('> ')) {
                          return (
                            <blockquote key={index} className="border-l-4 border-gray-300 pl-4 italic text-gray-600">
                              {paragraph.replace(/^>\s/, '')}
                            </blockquote>
                          );
                        }
                        return <p key={index} className="text-sm">{paragraph}</p>;
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* JSON Map Generation */}
            <Card>
              <CardHeader>
                <CardTitle>Generated JSON Map</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  type="button"
                  onClick={generateJsonMap}
                >
                  Generate JSON Map
                </Button>
                <Textarea
                  value={data.json_map}
                  onChange={(e) => setData('json_map', e.target.value)}
                  className="min-h-[12rem] bg-gray-50 font-mono text-sm"
                  readOnly
                />
              </CardContent>
            </Card>

            {/* Extract Response and Divisors (only show if editing) */}
            {isEditing && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Extract Response */}
                <Card>
                  <CardHeader>
                    <CardTitle>Extract Response</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {extractJob.response_payload ? (
                      <div className="bg-gray-50 border border-gray-200 rounded-md p-4 h-96 overflow-auto">
                        <pre className="text-sm whitespace-pre-wrap break-words">
                          {JSON.stringify(extractJob.response_payload, null, 2)}
                        </pre>
                      </div>
                    ) : (
                      <div className="bg-gray-50 border border-gray-200 rounded-md p-4 text-center text-gray-600">
                        No Reducto Extract Response available.
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Divisors */}
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle>Divisors</CardTitle>
                      <Button asChild variant="outline" size="sm">
                        <Link href={`/documents/${document.id}/extract_jobs/${extractJob.id}/divisors`}>
                          Edit Divisors
                        </Link>
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="h-96 overflow-auto">
                      {extractJob.divisors && extractJob.divisors.length > 0 ? (
                        <div className="space-y-3">
                          {extractJob.divisors.map((divisor) => (
                            <div
                              key={divisor.id}
                              className="p-3 border border-gray-200 rounded-md bg-gray-50"
                            >
                              <div className="font-semibold">
                                {divisor.divisor_type.charAt(0).toUpperCase() + divisor.divisor_type.slice(1)}: {divisor.title}
                              </div>
                              <div className="text-sm text-gray-600 mt-1">
                                Preview: {divisor.content_preview}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center text-gray-600">
                          No divisors have been created for this extract job.
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end">
              <Button type="submit" disabled={processing}>
                {processing ? 'Saving...' : isEditing ? 'Update' : 'Save'}
              </Button>
            </div>
          </form>

          {/* Navigation (only show if editing) */}
          {isEditing && (previousExtractJob || nextExtractJob) && (
            <>
              <Separator />
              <div className="flex justify-between items-center">
                <div>
                  {previousExtractJob && (
                    <Button asChild variant="outline">
                      <Link href={`/documents/${document.id}/extract_jobs/${previousExtractJob.id}/edit`}>
                        <ChevronLeft className="h-4 w-4 mr-2" />
                        Previous Extract Job
                      </Link>
                    </Button>
                  )}
                </div>
                <div>
                  {nextExtractJob && (
                    <Button asChild variant="outline">
                      <Link href={`/documents/${document.id}/extract_jobs/${nextExtractJob.id}/edit`}>
                        Next Extract Job
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </Link>
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}